import * as React from 'react';
import { useEffect, useState } from 'react';
import * as alpha from '@journeyapps-platform/types-alpha';
import { CommonAppCreationParams } from '@journeyapps-platform/types-alpha';
import { SourceType } from '@journeyapps-platform/types-source-service';
import { CreateAppStep1Widget } from './CreateAppStep1Widget';
import { JourneyNavigationHandler, Routes } from '../../../routes';
import { CreateAppStep2Widget } from './CreateAppStep2Widget';
import { useSystem } from '../../../hooks/useSystem';
import { CreateAppStep3Widget } from './CreateAppStep3Widget';
import { useOrgStore } from '../../../hooks/useOrgStore';
import { observer } from 'mobx-react';
import { SerializedAppResource, VCS_MODE } from '@journeyapps-platform/types-accounts-hub';
import { useDialogStore } from '../../../hooks/useDialogService';
import { useAllowed } from '../../../hooks/useAllowed';
import { AppModel, OrganizationModel } from '@journeyapps-platform/lib-journey-core';
import { CreateAppStep0Widget } from './CreateAppStep0Widget';
import { getAppUrl, Product, PRODUCT_CONFIG_MAP } from '@journeyapps-platform/types-accounts';
import { ENV } from '../../../env';
import { LoaderSpinner } from '../../../widgets/LoaderWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import styled from '@emotion/styled';
import { Region } from '../../../utils/environments';
import { AppModelAction } from '@journeyapps-platform/cardinal-catalog';

export enum CreateAppStep {
  STEP_TYPE = 'step0',
  STEP_NAME = 'step1',
  STEP_SOURCE = 'step2',
  STEP_TEMPLATE = 'step3',
  CREATE = 'create'
}

export const getSteps = (type: Product) => {
  if (type === Product.POWERSYNC) {
    return ['Project details', 'Version Control'];
  }
  return ['Project details', 'Version Control', 'Template'];
};

export enum TemplateOption {
  BLANK = 'Blank',
  TEMPLATE = 'Template',
  CLONE = 'Clone existing app'
}

export const VersionControl = {
  [VCS_MODE.BASIC]: 'Basic (Revisions)',
  [VCS_MODE.ADVANCED]: 'Advanced (Git)'
};

export const GitProvider = {
  [SourceType.Internal]: 'JourneyApps',
  [SourceType.GitHub]: 'GitHub',
  [SourceType.AzureDevops]: 'Azure Repos'
};

export type CreateAppPayload = Omit<alpha.CreateAppParams, 'region'> & {
  token?: string;
  step: CreateAppStep;
  region: Region;
  loading?: boolean;
  original_app_id?: string;
  type: Product;
  trial: boolean;
};

export const CreateAppPage: React.FC<{
  handler: JourneyNavigationHandler<Routes.CREATE_APP>;
  org: OrganizationModel;
}> = observer((props) => {
  const system = useSystem();
  const orgStore = useOrgStore();
  const { params, navigate } = props.handler;
  const dialogStore = useDialogStore();

  const [tempApp] = useState<AppModel>(() => {
    return system.appsStore.generate({
      id: '',
      auth_scope_id: '',
      name: '',
      region: Region.US,
      org_id: params.org_id,
      features: []
    });
  });

  const isAllowed = useAllowed({ action: AppModelAction.CREATE, resource: tempApp });

  const showErrorDialog = async () => {
    return dialogStore.errorDialog({
      subTitle: 'Failed to create new project',
      errorMessage: `Oh no, something went wrong and this project couldn't be created. Please try again in a few minutes.`
    });
  };

  useEffect(() => {
    (async () => {
      const org = await orgStore.resolve(params.org_id);
      await org.loadApps();
    })();
  }, []);

  const [payload, setPayload] = useState<CreateAppPayload>(() => {
    return {
      step: CreateAppStep.STEP_TYPE,
      name: '',
      org_id: params.org_id,
      vcs_mode: VCS_MODE.BASIC,
      template_id: 'standard',
      source: null as any,
      region: null as any,
      type: Product.POWERSYNC,
      payload: false,
      trial: false
    };
  });

  useEffect(() => {
    if (payload.step !== CreateAppStep.CREATE) {
      return;
    }
    (async () => {
      if (!isAllowed) {
        await dialogStore.errorDialog({
          title: 'Not Allowed',
          errorMessage: `You do not have permission to create a new app. Ask your administrator to create this app, or to elevate your permissions.`
        });
        return;
      }

      const alpha = system.clientProvisioner.provisionAlphaClient();

      let commonParams: CommonAppCreationParams = {
        name: payload.name,
        org_id: payload.org_id,
        region: payload.region,
        source: payload.source,
        create_repo: payload.create_repo,
        token: payload.token,
        vcs_mode: payload.vcs_mode
      };

      try {
        let app: SerializedAppResource;
        // moving an app
        if (payload.original_app_id) {
          app = await alpha.cloneApp({
            ...commonParams,
            original_app_id: payload.original_app_id
          });
        }
        // creating an app
        else {
          app = await alpha.createApp({
            ...commonParams,
            features: PRODUCT_CONFIG_MAP[payload.type].new_app_features,
            template_id: payload.template_id
          });
        }
        if (!app) {
          console.error('Empty project returned in response: ', app);
          await showErrorDialog();
          return;
        }
        orgStore.getOrg(params.org_id).then((o) => o.loadApps());
        navigate(Routes.MANAGE_APPS, params);
        window.location = getAppUrl({
          app,
          env: ENV
        }) as any;
      } catch (err) {
        console.error('Failed to create project: ', err);
        await showErrorDialog();
      }
    })();
  }, [payload.step]);

  if (payload.step === CreateAppStep.CREATE) {
    return (
      <CenterFormWidget noLogo={true} primaryLabel="Creating project..." orientation={CenterFormOrientation.ROW}>
        <S.CenterContent>
          <LoaderSpinner />
        </S.CenterContent>
      </CenterFormWidget>
    );
  }

  if (payload.step === CreateAppStep.STEP_TYPE) {
    return (
      <CreateAppStep0Widget
        org_id={props.org.id}
        payload={payload}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
        next={(p) => {
          setPayload({
            ...payload,
            ...p,
            step: CreateAppStep.STEP_NAME
          });
        }}
      />
    );
  }

  if (payload.step === CreateAppStep.STEP_NAME) {
    return (
      <CreateAppStep1Widget
        payload={payload}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
        next={(p) => {
          setPayload({
            ...payload,
            ...p,
            step: CreateAppStep.STEP_SOURCE
          });
        }}
      />
    );
  }

  if (payload.step === CreateAppStep.STEP_SOURCE) {
    return (
      <CreateAppStep2Widget
        org={props.org}
        handler={props.handler}
        payload={payload}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
        back={() => {
          setPayload({ ...payload, step: CreateAppStep.STEP_NAME });
        }}
        updatePayload={async (p, next = false) => {
          // just updating
          if (!next) {
            return setPayload({
              ...payload,
              ...p,
              step: CreateAppStep.STEP_SOURCE
            });
          }

          // check to see if we need to select a template still or if we can just use the first one
          const languages = PRODUCT_CONFIG_MAP[payload.type].languages;
          if (languages.length === 1) {
            const client = system.clientProvisioner.provisionAlphaClient();
            const res = await client.getTemplates({
              language: languages[0]
            });

            if (res.length === 1) {
              return setPayload({
                ...payload,
                ...p,
                template_id: res[0].id,
                step: CreateAppStep.CREATE,
                loading: true
              });
            }
          }

          setPayload({
            ...payload,
            ...p,
            step: CreateAppStep.STEP_TEMPLATE
          });
        }}
      />
    );
  }
  if (payload.step === CreateAppStep.STEP_TEMPLATE) {
    return (
      <CreateAppStep3Widget
        payload={payload}
        onCancel={() => {
          navigate(Routes.MANAGE_APPS, params);
        }}
        back={() => {
          setPayload({ ...payload, step: CreateAppStep.STEP_SOURCE });
        }}
        updatePayload={async (p) => {
          await new Promise<void>((resolve) => {
            setPayload({
              ...payload,
              ...p
            });
            resolve();
          });
        }}
        next={async () => {
          setPayload({
            ...payload,
            step: CreateAppStep.CREATE,
            loading: true
          });
        }}
      />
    );
  }
  return <div>Empty</div>;
});

namespace S {
  export const CenterContent = styled.div`
    display: flex;
    align-content: center;
    justify-content: center;
    padding: 20px;
  `;
}
